/**
 * useAnalytics — React hook for event tracking.
 *
 * Returns a stable { track, flush } API backed by a module-level singleton
 * AnalyticsTracker.  The singleton is created lazily on first use so that
 * SSR environments (where window is undefined) never instantiate a tracker.
 *
 * Callers must set up the singleton before using the hook:
 *
 *   import { initAnalyticsTracker } from '@ecommerce/analytics';
 *   initAnalyticsTracker(loadAnalyticsConfig());
 *
 * Calling track() before init is a no-op.
 */

import { useCallback } from 'react';
import { AnalyticsTracker } from '../tracker/analytics-tracker';
import type { AnalyticsConfig, AnalyticsTrackOptions, JsonObject } from '../types';

// ---------------------------------------------------------------------------
// Module-level singleton
// ---------------------------------------------------------------------------

let trackerInstance: AnalyticsTracker | null = null;

/**
 * Initialises (or re-initialises) the module-level tracker singleton.
 * Call this once at application startup — typically in your root component or
 * `_app.tsx` / `layout.tsx`.
 */
export function initAnalyticsTracker(config: AnalyticsConfig): void {
  // Tear down any existing tracker cleanly before creating a new one
  if (trackerInstance !== null) {
    trackerInstance.destroy();
  }
  trackerInstance = new AnalyticsTracker(config);
}

/**
 * Exposes the singleton for direct use outside of React (e.g. in service
 * modules).  Returns null if the tracker has not been initialised yet.
 */
export function getAnalyticsTracker(): AnalyticsTracker | null {
  return trackerInstance;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Provides `track` and `flush` backed by the module-level singleton.
 *
 * @example
 * const { track } = useAnalytics();
 * track(EVENT_TYPES.PRODUCT_VIEWED, { product_id: '123', ... });
 */
export function useAnalytics(): {
  track: <T extends JsonObject>(
    eventType: string,
    data: T,
    options?: AnalyticsTrackOptions,
  ) => void;
  flush: () => Promise<void>;
} {
  const track = useCallback(
    <T extends JsonObject>(
      eventType: string,
      data: T,
      options?: AnalyticsTrackOptions,
    ): void => {
      if (options) {
        trackerInstance?.track(eventType, data, options);
      } else {
        trackerInstance?.track(eventType, data);
      }
    },
    []
  );

  const flush = useCallback(async (): Promise<void> => {
    await trackerInstance?.flush();
  }, []);

  return { track, flush };
}
